import { type QueryClient, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { paths } from '@ers/sdk';
import { api, call, ifMatch } from './client';
import { ApiError } from './errors';
import { qk } from './keys';
import type { Comment, CreateTaskInput, ProjectDetails, Task, UpdateTaskInput } from './types';

export type TaskListParams = NonNullable<paths['/api/v1/tasks']['get']['parameters']['query']>;

const MAX_PAGES = 5; // до 500 задач на подання (вимога швидкодії розділу 10)

export async function fetchAllTasks(query: TaskListParams): Promise<Task[]> {
  const out: Task[] = [];
  let cursor: string | undefined;
  for (let i = 0; i < MAX_PAGES; i++) {
    const page = await call(api.GET('/api/v1/tasks', { params: { query: { ...query, limit: 100, cursor } } }));
    out.push(...page.data);
    if (!page.nextCursor) break;
    cursor = page.nextCursor;
  }
  return out;
}

export function useTaskList(params: TaskListParams, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: qk.tasks(params as Record<string, unknown>),
    queryFn: () => fetchAllTasks(params),
    staleTime: 30_000,
    enabled: opts.enabled ?? true,
  });
}

export function taskQueryOptions(id: string) {
  return {
    queryKey: qk.task(id),
    queryFn: () => call(api.GET('/api/v1/tasks/{id}', { params: { path: { id } } })),
    staleTime: 15_000,
  };
}

export function useTask(id: string | null | undefined) {
  return useQuery({ ...taskQueryOptions(id ?? ''), enabled: !!id });
}

/** Кілька задач за ідентифікаторами (залежності). Недоступні — з помилкою 404. */
export function useTasksByIds(ids: string[]) {
  const key = ids.join(',');
  const list = useMemo(() => (key ? key.split(',') : []), [key]);
  const combine = useCallback(
    (results: { data?: Task; error: unknown; isLoading: boolean }[]) =>
      results.map((r, i) => ({ id: list[i]!, task: r.data, error: r.error as ApiError | null, isLoading: r.isLoading })),
    [list],
  );
  return useQueries({ queries: list.map((id) => ({ ...taskQueryOptions(id), retry: false })), combine });
}

export function useSubtasks(id: string | null | undefined) {
  return useQuery({
    queryKey: qk.taskPart(id ?? '', 'subtasks'),
    queryFn: async () => (await call(api.GET('/api/v1/tasks/{id}/subtasks', { params: { path: { id: id! } } }))).data,
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useComments(id: string | null | undefined) {
  return useQuery({
    queryKey: qk.taskPart(id ?? '', 'comments'),
    queryFn: async () => (await call(api.GET('/api/v1/tasks/{id}/comments', { params: { path: { id: id! } } }))).data,
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useActivity(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.taskPart(id ?? '', 'activity'),
    queryFn: async () => (await call(api.GET('/api/v1/tasks/{id}/activity', { params: { path: { id: id! } } }))).data,
    enabled: !!id && enabled,
    staleTime: 15_000,
  });
}

export function useAttachments(id: string | null | undefined) {
  return useQuery({
    queryKey: qk.taskPart(id ?? '', 'attachments'),
    queryFn: async () => (await call(api.GET('/api/v1/tasks/{id}/attachments', { params: { path: { id: id! } } }))).data,
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useMySummary() {
  return useQuery({
    queryKey: qk.mySummary,
    queryFn: () => call(api.GET('/api/v1/my/summary')),
    staleTime: 30_000,
  });
}

/** Оновлює задачу в усіх кешах (картка, переліки) і фоново перезавантажує переліки. */
export function applyTask(qc: QueryClient, task: Task, opts: { refetchLists?: boolean } = {}) {
  qc.setQueryData(qk.task(task.id), task);
  qc.setQueriesData<Task[]>({ queryKey: qk.tasksAll }, (list) => (Array.isArray(list) ? list.map((t) => (t.id === task.id ? task : t)) : list));
  if (opts.refetchLists !== false) {
    void qc.invalidateQueries({ queryKey: qk.tasksAll });
    void qc.invalidateQueries({ queryKey: qk.mySummary });
    for (const m of task.memberships) void qc.invalidateQueries({ queryKey: qk.project(m.projectId) });
    if (task.parentId) void qc.invalidateQueries({ queryKey: qk.taskPart(task.parentId, 'subtasks') });
  }
  void qc.invalidateQueries({ queryKey: qk.taskPart(task.id, 'activity') });
}

/** Найсвіжіша відома версія задачі (для If-Match). */
export function latestVersion(qc: QueryClient, task: Task): number {
  const cached = qc.getQueryData<Task>(qk.task(task.id));
  return Math.max(cached?.version ?? 0, task.version);
}

function onVersionConflict(qc: QueryClient, id: string, e: unknown) {
  if (e instanceof ApiError && e.status === 412) {
    void qc.invalidateQueries({ queryKey: qk.task(id) });
    void qc.invalidateQueries({ queryKey: qk.tasksAll });
  }
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTaskInput) => call(api.POST('/api/v1/tasks', { body })),
    onSuccess: (task) => applyTask(qc, task),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ task, body }: { task: Task; body: UpdateTaskInput }) =>
      call(api.PATCH('/api/v1/tasks/{id}', { params: { path: { id: task.id } }, body, headers: ifMatch(latestVersion(qc, task)) })),
    onSuccess: (task) => applyTask(qc, task),
    onError: (e, v) => onVersionConflict(qc, v.task.id, e),
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, result }: { id: string; result?: string }) =>
      call(api.POST('/api/v1/tasks/{id}/complete', { params: { path: { id } }, body: { result } })),
    onSuccess: (task) => {
      applyTask(qc, task);
      void qc.invalidateQueries({ queryKey: qk.taskPart(task.id, 'comments') });
    },
  });
}

export function useReopenTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      call(api.POST('/api/v1/tasks/{id}/reopen', { params: { path: { id } }, body: { reason } })),
    onSuccess: (task) => {
      applyTask(qc, task);
      void qc.invalidateQueries({ queryKey: qk.taskPart(task.id, 'comments') });
    },
  });
}

export type ApprovalDecision = 'approved' | 'changes_requested' | 'rejected';

export function useDecideApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, comment }: { id: string; decision: ApprovalDecision; comment?: string }) =>
      call(api.POST('/api/v1/tasks/{id}/approval', { params: { path: { id } }, body: { decision, comment } })),
    onSuccess: (task) => {
      applyTask(qc, task);
      void qc.invalidateQueries({ queryKey: qk.taskPart(task.id, 'comments') });
      void qc.invalidateQueries({ queryKey: qk.inboxAll });
    },
  });
}

export function useArchiveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => call(api.DELETE('/api/v1/tasks/{id}', { params: { path: { id } } })),
    onSuccess: (task) => applyTask(qc, task),
  });
}

/** Відповіді на умови переходу етапу (модулі automation і legal читають їх з поля checks). */
export type TransitionChecks = {
  answers?: Record<string, boolean | string>;
  /** Формула коментаря; залучений працівник — involvedPersonId (однаково для automation і legal), зовнішня особа — involvedPerson. */
  comment?: { reason?: string; expectedAction?: string; involvedPersonId?: string; involvedPerson?: string; controlDate?: string; nextStep?: string };
  result?: string;
  /** Юридичний результат (модуль legal вимагає його під час закриття юридичної частини). */
  legalResult?: string;
  /** Новий плановий строк (legal: вихід з «Підвисло»). */
  plannedDueOn?: string;
  /** Дані подання (legal: перехід у «Контроль (рішення)»). */
  control?: { submittedOn?: string; authority?: string; expectedBy?: string; controlMethod?: string };
};

export type MoveBody = { projectId: string; sectionId: string | null; beforeTaskId?: string; afterTaskId?: string; checks?: TransitionChecks };

export function useMoveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['move-task'],
    mutationFn: ({ id, body }: { id: string; body: MoveBody }) =>
      // Поле checks ще не описане в типах SDK — сервер його приймає (POST /tasks/{id}/move).
      call(api.POST('/api/v1/tasks/{id}/move', { params: { path: { id } }, body: body as never })),
    onSuccess: (task) => applyTask(qc, task, { refetchLists: false }),
    onSettled: (_t, _e, v) => {
      void qc.invalidateQueries({ queryKey: qk.tasksAll });
      void qc.invalidateQueries({ queryKey: qk.project(v.body.projectId) });
    },
  });
}

export function useAddTaskToProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, projectId, sectionId }: { id: string; projectId: string; sectionId?: string }) =>
      call(api.POST('/api/v1/tasks/{id}/projects', { params: { path: { id } }, body: { projectId, sectionId } })),
    onSuccess: (task) => applyTask(qc, task),
  });
}

export function useRemoveTaskFromProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, projectId }: { id: string; projectId: string }) =>
      call(api.DELETE('/api/v1/tasks/{id}/projects/{projectId}', { params: { path: { id, projectId } } })),
    onSuccess: (task) => applyTask(qc, task),
  });
}

/** Роль у задачі: учасник (працює, змінює поля) або спостерігач (перегляд, коментарі, сповіщення). */
export type ParticipantRole = 'collaborator' | 'observer';

/** Учасники задачі (collaboratorIds; ще немає в типах SDK — читаємо обережно). */
export function collaboratorsOf(task: Task): string[] {
  return (task as Task & { collaboratorIds?: string[] }).collaboratorIds ?? [];
}

/** Спостерігачі = усі, хто стежить, крім учасників і виконавця. */
export function observersOf(task: Task): string[] {
  const collab = new Set(collaboratorsOf(task));
  return task.followerIds.filter((id) => !collab.has(id) && id !== task.assigneeId);
}

export function useFollower() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userId, add, role }: { id: string; userId: string; add: boolean; role?: ParticipantRole }) =>
      add
        ? call(api.POST('/api/v1/tasks/{id}/followers', { params: { path: { id } }, body: (role ? { userId, role } : { userId }) as never }))
        : call(api.DELETE('/api/v1/tasks/{id}/followers/{userId}', { params: { path: { id, userId } } })),
    onSuccess: (task) => applyTask(qc, task, { refetchLists: false }),
  });
}

export function useDependency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dependsOnId, add }: { id: string; dependsOnId: string; add: boolean }) =>
      add
        ? call(api.POST('/api/v1/tasks/{id}/dependencies', { params: { path: { id } }, body: { dependsOnId } }))
        : call(api.DELETE('/api/v1/tasks/{id}/dependencies/{dependsOnId}', { params: { path: { id, dependsOnId } } })),
    onSuccess: (task) => applyTask(qc, task, { refetchLists: false }),
  });
}

export function useTaskTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tagId, add }: { id: string; tagId: string; add: boolean }) =>
      add
        ? call(api.POST('/api/v1/tasks/{id}/tags', { params: { path: { id } }, body: { tagId } }))
        : call(api.DELETE('/api/v1/tasks/{id}/tags/{tagId}', { params: { path: { id, tagId } } })),
    onSuccess: (task) => applyTask(qc, task, { refetchLists: false }),
  });
}

export function useAddComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { body: string; mentions?: string[]; allowSensitive?: boolean }) =>
      call(api.POST('/api/v1/tasks/{id}/comments', { params: { path: { id: taskId } }, body })),
    onSuccess: (c) => {
      qc.setQueryData<Comment[]>(qk.taskPart(taskId, 'comments'), (list) => (list ? [...list, c] : [c]));
      void qc.invalidateQueries({ queryKey: qk.taskPart(taskId, 'comments') });
      void qc.invalidateQueries({ queryKey: qk.task(taskId) });
    },
  });
}

export function useEditComment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string | null }) =>
      body === null
        ? call(api.DELETE('/api/v1/comments/{id}', { params: { path: { id } } }))
        : call(api.PATCH('/api/v1/comments/{id}', { params: { path: { id } }, body: { body } })),
    onSuccess: (c) => {
      qc.setQueryData<Comment[]>(qk.taskPart(taskId, 'comments'), (list) => list?.map((x) => (x.id === c.id ? c : x)));
    },
  });
}

export function useAddAttachment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; url: string; kind?: 'link' | 'drive'; driveFileId?: string }) =>
      call(api.POST('/api/v1/tasks/{id}/attachments', { params: { path: { id: taskId } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.taskPart(taskId, 'attachments') }),
  });
}

export function useRemoveAttachment(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) =>
      call(api.DELETE('/api/v1/tasks/{id}/attachments/{attachmentId}', { params: { path: { id: taskId, attachmentId } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.taskPart(taskId, 'attachments') }),
  });
}

/** Етап задачі в проєкті: назва з деталей проєкту. */
export function membershipSection(task: Task, project: ProjectDetails | undefined) {
  const m = task.memberships.find((x) => x.projectId === project?.id);
  if (!m || !project) return null;
  return project.sections.find((s) => s.id === m.sectionId) ?? null;
}
